import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { fmtCur, fmtCurCompact, fmtNum } from "../lib/format";
import { useCurrencyMode } from "../lib/useCurrencyMode";
import { useRefresh } from "../contexts/RefreshContext";
import { Factory, Clock, ArrowRight } from "@phosphor-icons/react";
import SearchSelect from "../components/SearchSelect";

function KPITile({ label, value, sub, accent, onClick, testid, cta = "Explore" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="kpi-tile" style={{ "--kpi-accent": accent }}>
      <span className="kpi-tile-label">{label}</span>
      <div className="kpi-tile-value">{value}</div>
      {sub && <div className="kpi-tile-sub">{sub}</div>}
      <span className="kpi-tile-cta">{cta} <ArrowRight size={11} weight="bold" /></span>
    </button>
  );
}

export default function MsDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [plant, setPlant] = useState("");
  const [plants, setPlants] = useState([]);
  const [customer, setCustomer] = useState("");
  const [customers, setCustomers] = useState([]);
  const [missing, setMissing] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [sales, setSales] = useState(null);
  const [oov, setOov] = useState(null);
  const [priceMode, setPriceMode] = useState("invoice"); // "invoice" (new truth) | "po"
  const mode = useCurrencyMode();
  const { tick } = useRefresh();

  useEffect(() => {
    api.get("/ms/plants").then((r) => setPlants(r.data)).catch(() => {});
    api.get("/ms/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (plant) qs.set("plant", plant);
    if (customer) qs.set("customer", customer);
    if (mode) qs.set("mode", mode);
    api.get(`/ms/dashboard?${qs.toString()}`).then((r) => setData(r.data)).catch(() => {});
    // per-customer breakdown of missing dates (not scoped by customer filter — it IS the breakdown)
    const mq = new URLSearchParams();
    if (plant) mq.set("plant", plant);
    if (mode) mq.set("mode", mode);
    api.get(`/ms/missing-dates?${mq.toString()}`).then((r) => setMissing(r.data)).catch(() => setMissing(null));
    const sq = new URLSearchParams();
    if (plant) sq.set("plant", plant);
    if (mode) sq.set("mode", mode);
    api.get(`/ms/sales-monthly?${sq.toString()}`).then((r) => setSales(r.data)).catch(() => setSales(null));
    // Open-order value using Partyi rate (≠0) else poi rate — scoped by customer only.
    const oq = new URLSearchParams();
    if (customer) oq.set("customer", customer);
    setOov(null);
    api.get(`/dashboard/open-order-value?${oq.toString()}`).then((r) => setOov(r.data)).catch(() => setOov(null));
  }, [plant, customer, mode, tick]);

  if (!data) return <Layout breadcrumb={["Home", "Dashboard"]}>Loading…</Layout>;

  const buckets = data.aging_buckets || {};
  const totalAged = (buckets["0-30"] || 0) + (buckets["31-60"] || 0) + (buckets["61-90"] || 0) + (buckets["90+"] || 0);
  const ageColor = { "0-30": "#10B981", "31-60": "#F59E0B", "61-90": "#F97316", "90+": "#EF4444" };

  const dueBuckets = data.due_buckets || {};
  const dueKeys = ["overdue", "0-30", "31-60", "61-90", "90+"];
  const totalDue = dueKeys.reduce((s, k) => s + (dueBuckets[k] || 0), 0);
  const dueColor = { "overdue": "#B91C1C", "0-30": "#10B981", "31-60": "#F59E0B", "61-90": "#F97316", "90+": "#EF4444" };
  const dueLabel = { "overdue": "Overdue", "0-30": "0–30d", "31-60": "31–60d", "61-90": "61–90d", "90+": "90+d" };

  return (
    <Layout breadcrumb={["Home", "Open Order Dashboard"]}>
      <div className="jde-page">
        <div className="jde-toolbar" data-testid="dashboard-toolbar">
          <div className="jde-toolbar-section">
            <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>CUSTOMER</span>
            <div style={{ width: 260 }}>
              <SearchSelect testid="filter-customer" value={customer} onChange={setCustomer}
                placeholder="All customers"
                options={customers.map((c) => ({ value: c.name, label: c.name, sub: c.ms_pcode }))} />
            </div>
          </div>
          <div className="jde-toolbar-section">
            <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>PLANT</span>
            <select className="jde-input" value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="filter-plant">
              <option value="">All plants</option>
              {plants.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {customer && (
            <span className="muted" style={{ fontSize: 11 }} data-testid="dashboard-scope">
              Showing data for <strong>{customer}</strong>
            </span>
          )}
        </div>

        {/* KPI TILES — every tile opens the interactive Explorer */}
        {(() => {
          const q = new URLSearchParams();
          if (plant) q.set("plant", plant);
          if (customer) q.set("customer", customer);
          const go = (params = {}) => {
            const p = new URLSearchParams(q);
            Object.entries(params).forEach(([k, v]) => v && p.set(k, v));
            const s = p.toString();
            nav(`/explorer${s ? `?${s}` : ""}`);
          };
          const goSales = () => {
            const s = q.toString();
            nav(`/sales${s ? `?${s}` : ""}`);
          };
          const tm = sales?.this_month;
          return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
              <KPITile testid="kpi-open-pos" label="Open POs" value={fmtNum(data.open_po_count)}
                sub={`${fmtNum(data.ordered_qty)} units ordered`} accent="#2A5C9B"
                cta="View by customer" onClick={() => go({ metric: "value" })} />
              <KPITile testid="kpi-sales-month" label="Sales · This Month"
                value={tm ? fmtCurCompact(tm.actual_inr, "INR") : "—"}
                sub={tm ? `Expected ${fmtCurCompact(tm.expected_inr, "INR")} (by due date)` : "shipped so far"}
                accent="#0369A1" cta="Monthly trend" onClick={goSales} />
            </div>
          );
        })()}

        {/* OPEN ORDER VALUE — single figure with a PO / Invoice price toggle */}
        <div data-testid="oov-panel" style={{ background: "white", border: "1px solid var(--jde-border,#e2e8f0)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Open Order Value</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div data-testid="oov-toggle" style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 999, padding: 3 }}>
                {[["po", "PO Price"], ["invoice", "Invoice Price"]].map(([k, lbl]) => (
                  <button key={k} data-testid={`oov-mode-${k}`} onClick={() => setPriceMode(k)}
                    style={{ padding: "6px 16px", fontSize: 12.5, fontWeight: 700, borderRadius: 999, border: "none", cursor: "pointer",
                      background: priceMode === k ? "#2A5C9B" : "transparent", color: priceMode === k ? "white" : "#475569" }}>{lbl}</button>
                ))}
              </div>
              {oov?.fx?.usd_inr && <span style={{ fontSize: 11, color: "#94a3b8" }}>@ ₹{oov.fx.usd_inr}/$</span>}
            </div>
          </div>
          {!oov && <div style={{ color: "#94a3b8", fontSize: 13, padding: "10px 0" }}>Calculating…</div>}
          {oov && (() => {
            const m = oov[priceMode] || {};
            const bc = m.by_currency || {};
            return (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 14 }}>
                  <div style={{ padding: 12, borderRadius: 10, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", letterSpacing: .3 }}>USD (export)</div>
                    <div data-testid="oov-usd" style={{ fontSize: 22, fontWeight: 800, color: "#1e3a8a" }}>{fmtCurCompact(bc.USD || 0, "USD")}</div>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: "#ecfdf5", border: "1px solid #a7f3d0" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", letterSpacing: .3 }}>INR (domestic)</div>
                    <div data-testid="oov-inr" style={{ fontSize: 22, fontWeight: 800, color: "#065f46" }}>{fmtCurCompact(bc.INR || 0, "INR")}</div>
                  </div>
                  <div style={{ padding: 12, borderRadius: 10, background: "#faf5ff", border: "1px solid #e9d5ff" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9333ea", letterSpacing: .3 }}>Total (all → ₹)</div>
                    <div data-testid="oov-total" style={{ fontSize: 22, fontWeight: 800, color: "#6b21a8" }}>{fmtCurCompact(m.cumulative_inr || 0, "INR")}</div>
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                    <thead><tr>
                      {["Customer", "Open Qty", "Value"].map((h, i) => (
                        <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "6px 10px", color: "#64748b", fontWeight: 700, borderBottom: "1px solid #e2e8f0", fontSize: 11 }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {(oov.customers || []).filter((c) => Object.values(c[priceMode] || {}).some((v) => Math.abs(v) > 0.5)).map((c, i) => (
                        <tr key={i} data-testid={`oov-row-${c.pcode}`}>
                          <td style={{ padding: "6px 10px", fontWeight: 600 }}>{c.customer_name}</td>
                          <td style={{ padding: "6px 10px", textAlign: "right", color: "#64748b" }} className="text-mono">{fmtNum(c.open_qty)}</td>
                          <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }} className="text-mono">
                            {Object.entries(c[priceMode]).filter(([, v]) => Math.abs(v) > 0.5).map(([k, v]) => fmtCur(v, k)).join(" + ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>

        {/* AGING + PLANT BREAKDOWN */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginBottom: 18 }}>
          <div className="jde-panel" data-testid="aging-panel">
            <div className="jde-panel-header">
              <div className="jde-panel-title"><Clock size={13} weight="bold" /> Open Orders by Age</div>
            </div>
            <div className="jde-panel-body">
              {["0-30", "31-60", "61-90", "90+"].map((k) => {
                const v = buckets[k] || 0;
                const pct = totalAged ? (v / totalAged * 100) : 0;
                const go = () => nav(`/pos?age=${encodeURIComponent(k)}${plant ? `&plant=${encodeURIComponent(plant)}` : ""}`);
                return (
                  <div key={k} role="button" tabIndex={0}
                    onClick={go}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
                    data-testid={`aging-bar-${k}`}
                    title={`View ${v} PO(s) aged ${k} days`}
                    style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, cursor: "pointer" }}>
                    <div style={{ width: 60, fontFamily: "var(--jde-font-mono)", fontSize: 12, fontWeight: 600 }}>{k}d</div>
                    <div style={{ flex: 1, height: 18, background: "#F3F4F6", borderRadius: 2, position: "relative", overflow: "hidden" }}>
                      <div style={{ position: "absolute", inset: 0, width: `${pct}%`, background: ageColor[k], transition: "width 300ms" }} />
                    </div>
                    <div style={{ width: 60, textAlign: "right", fontFamily: "var(--jde-font-mono)", fontSize: 13, fontWeight: 700 }}>{fmtNum(v)}</div>
                  </div>
                );
              })}
              {buckets.no_date > 0 && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => setShowMissing((s) => !s)}
                    data-testid="no-date-toggle"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                             color: "#B45309", fontSize: 11, fontWeight: 600, textDecoration: "underline" }}
                  >
                    {buckets.no_date} PO(s) without a customer date (cust_po_date) — {showMissing ? "hide" : "breakdown"}
                  </button>
                  {showMissing && missing && (
                    <div data-testid="no-date-breakdown" style={{ marginTop: 8, border: "1px solid var(--jde-border)", borderRadius: 4, overflow: "hidden" }}>
                      <table className="jde-grid" style={{ margin: 0 }}>
                        <thead>
                          <tr><th>Customer</th><th style={{ textAlign: "right" }}>Missing PO date</th><th style={{ textAlign: "right" }}>Missing due date</th></tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const dmap = {};
                            (missing.deliv_date?.by_customer || []).forEach((r) => { dmap[r.customer_name] = r.count; });
                            const names = new Set([
                              ...(missing.cust_po_date?.by_customer || []).map((r) => r.customer_name),
                              ...(missing.deliv_date?.by_customer || []).map((r) => r.customer_name),
                            ]);
                            const cmap = {};
                            (missing.cust_po_date?.by_customer || []).forEach((r) => { cmap[r.customer_name] = r.count; });
                            return [...names].sort((a, b) => (cmap[b] || 0) - (cmap[a] || 0)).map((n) => (
                              <tr key={n} data-testid={`no-date-row-${n}`}>
                                <td><strong>{n}</strong></td>
                                <td className="text-mono" style={{ textAlign: "right", color: (cmap[n] || 0) ? "#B91C1C" : "#94A3B8" }}>{fmtNum(cmap[n] || 0)}</td>
                                <td className="text-mono" style={{ textAlign: "right", color: (dmap[n] || 0) ? "#B45309" : "#94A3B8" }}>{fmtNum(dmap[n] || 0)}</td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="jde-panel" data-testid="plant-panel">
            <div className="jde-panel-header">
              <div className="jde-panel-title"><Factory size={13} weight="bold" /> Plant Breakdown (Invoiced)</div>
            </div>
            <div className="jde-panel-body" style={{ padding: 0 }}>
              <table className="jde-grid">
                <thead><tr><th>Plant</th><th style={{ textAlign: "right" }}>Invoices</th><th style={{ textAlign: "right" }}>G Total</th></tr></thead>
                <tbody>
                  {(data.plant_breakdown || []).map((p) => {
                    const ccys = Object.entries(p.gtotal_by_ccy || {}).filter(([, v]) => v);
                    return (
                    <tr key={p.plant} data-testid={`plant-row-${p.plant}`}>
                      <td><span className="jde-badge confirmed">{p.plant}</span></td>
                      <td style={{ textAlign: "right", fontFamily: "var(--jde-font-mono)" }}>{fmtNum(p.invoices)}</td>
                      <td style={{ textAlign: "right", fontFamily: "var(--jde-font-mono)" }}>
                        {ccys.length === 0 ? "—" : ccys.map(([cc, v]) => (
                          <div key={cc} title={fmtCur(v, cc)}>{fmtCurCompact(v, cc)}</div>
                        ))}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* OPEN ORDERS BY DUE DATE */}
        <div className="jde-panel" data-testid="due-panel" style={{ marginBottom: 18 }}>
          <div className="jde-panel-header">
            <div className="jde-panel-title"><Clock size={13} weight="bold" /> Open Orders by Due Date</div>
          </div>
          <div className="jde-panel-body">
            {dueKeys.map((k) => {
              const v = dueBuckets[k] || 0;
              const pct = totalDue ? (v / totalDue * 100) : 0;
              const go = () => nav(`/pos?due=${encodeURIComponent(k)}${plant ? `&plant=${encodeURIComponent(plant)}` : ""}`);
              return (
                <div key={k} role="button" tabIndex={0}
                  onClick={go}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
                  data-testid={`due-bar-${k}`}
                  title={`View ${v} PO(s) due ${dueLabel[k]}`}
                  style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, cursor: "pointer" }}>
                  <div style={{ width: 72, fontFamily: "var(--jde-font-mono)", fontSize: 12, fontWeight: 600, color: k === "overdue" ? "#B91C1C" : "inherit" }}>{dueLabel[k]}</div>
                  <div style={{ flex: 1, height: 18, background: "#F3F4F6", borderRadius: 2, position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", inset: 0, width: `${pct}%`, background: dueColor[k], transition: "width 300ms" }} />
                  </div>
                  <div style={{ width: 60, textAlign: "right", fontFamily: "var(--jde-font-mono)", fontSize: 13, fontWeight: 700 }}>{fmtNum(v)}</div>
                </div>
              );
            })}
            {dueBuckets.no_date > 0 && (
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }} data-testid="due-no-date">
                {fmtNum(dueBuckets.no_date)} open PO(s) have no due date on any open line.
              </div>
            )}
          </div>
        </div>

        {/* TOP CUSTOMERS */}
        <div className="jde-panel" data-testid="top-customers-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title">Top 5 Customers by Open Value</div>
            <button type="button" data-testid="explore-all-customers"
              onClick={() => nav(`/explorer${plant ? `?plant=${encodeURIComponent(plant)}` : ""}`)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--omh-primary)",
                       fontSize: 11.5, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
              Explore all {data.customer_count ?? 0} <ArrowRight size={12} weight="bold" />
            </button>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <table className="jde-grid">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th style={{ textAlign: "right" }}>Open POs</th>
                  <th style={{ textAlign: "right" }}>Open Qty</th>
                  <th style={{ textAlign: "right" }}>Open Value</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data.top_customers || []).map((c) => {
                  const ccys = Object.entries(c.open_value_by_ccy || {}).filter(([, v]) => v);
                  return (
                  <tr key={c.customer_name} data-testid={`top-cust-${c.customer_name}`}>
                    <td><strong>{c.customer_name || "—"}</strong></td>
                    <td style={{ textAlign: "right", fontFamily: "var(--jde-font-mono)" }}>{fmtNum(c.po_count)}</td>
                    <td style={{ textAlign: "right", fontFamily: "var(--jde-font-mono)" }}>{fmtNum(c.open_qty)}</td>
                    <td style={{ textAlign: "right", fontFamily: "var(--jde-font-mono)", fontWeight: 600 }}
                        data-testid={`top-cust-value-${c.customer_name}`}>
                      {ccys.length === 0 ? "—" : ccys.map(([cc, v]) => (
                        <div key={cc} title={fmtCur(v, cc)}>{fmtCurCompact(v, cc)}</div>
                      ))}
                    </td>
                    <td>
                      <Link to={`/pos?customer=${encodeURIComponent(c.ms_pcode || c.customer_name)}`}
                        style={{ color: "var(--omh-primary)", fontSize: 11 }}>view POs ↗</Link>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </Layout>
  );
}
